import dayjs from 'dayjs'
import type { MonitoringActivity, RefData, SOCSampleWithDocuments } from '../../../types/apiModels'
import {
  monitoringSOCSamplingAreasFixedColumns,
  samplingAreasColumn,
  soilTexturesColumn,
} from '../SOCSampleDialog/useSOCSamplingAreasColumns'
import {
  dapColumn,
  dapMethodColumn,
  finalDepthColumn,
  initialDepthColumn,
  monitoringSOCColumn,
  monitoringSOCSitesFixedColumns,
} from '../SOCSampleDialog/useSOCSitesColumns'

export type ExportCellValue = string | number | null

export type ExportColumn = { key: string; header: string; numeric: boolean }

export type ExportSheet = {
  name: string
  columns: ExportColumn[]
  rows: Record<string, ExportCellValue>[]
}

export type SOCSampleExport = {
  fileName: string
  headerInfo: { label: string; value: string }[]
  sheets: ExportSheet[]
}

export type SelectOption = { id: string | number; label: string }

export type SOCSampleExportLookups = {
  farm: { name: string; hubName?: string | null }
  activities: MonitoringActivity[]
  strata: { id: string; name: string }[]
  dapMethods: RefData[]
  soilTextures: RefData[]
  samplingNumbers: RefData[]
  laboratories: SelectOption[]
  socProtocols: SelectOption[]
}

type GridColumnDefinition = { field: string; headerName: string; type?: string }

const UNRESOLVED_STRATUM = 'Sin estrato'

const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' })

const toExportColumn = ({ field, headerName, type }: GridColumnDefinition): ExportColumn => ({
  key: field,
  header: headerName.replace(/\n/g, ' '),
  numeric: type === 'number',
})

const sitesColumnsByField = new Map(
  [
    monitoringSOCColumn,
    dapMethodColumn,
    ...monitoringSOCSitesFixedColumns,
    initialDepthColumn,
    finalDepthColumn,
    dapColumn,
  ].map(column => [column.field, column as GridColumnDefinition]),
)

const pickSitesColumns = (fields: string[]) =>
  fields.map(field => toExportColumn(sitesColumnsByField.get(field)))

const STRATA_COLUMNS: ExportColumn[] = [
  samplingAreasColumn,
  ...monitoringSOCSamplingAreasFixedColumns,
  soilTexturesColumn,
].map(column => toExportColumn(column as GridColumnDefinition))

const SITES_COLUMNS: ExportColumn[] = [
  { key: 'stratum', header: 'Estrato', numeric: false },
  { key: 'site', header: 'Sitio', numeric: false },
  ...pickSitesColumns([
    'monitoringSOCActivityId',
    'name',
    'dapMethodId',
    'initialDepth',
    'finalDepth',
    'sampleVolume',
    'wbFirstReplicaPercentage',
    'wbSecondReplicaPercentage',
    'caFirstReplicaPercentage',
    'caSecondReplicaPercentage',
    'lecoFirstReplicaPercentage',
    'lecoSecondReplicaPercentage',
    'dryWeight',
    'gravelWeight',
    'gravelVolume',
    'dap',
  ]),
]

const labelById = <T extends { id: string | number }>(items: T[], getLabel: (item: T) => string) =>
  new Map(items.map(item => [String(item.id), getLabel(item)]))

const resolve = (labels: Map<string, string>, id: unknown) =>
  id === null || id === undefined ? '' : (labels.get(String(id)) ?? '')

// Sequelize serializes DECIMAL columns as strings; the sheet must hold real numbers.
const toCellValue = (value: unknown, numeric: boolean): ExportCellValue => {
  if (value === null || value === undefined || value === '') return null
  if (!numeric) return String(value)
  const parsed = Number(value)
  return Number.isNaN(parsed) ? String(value) : parsed
}

const toRow = (
  columns: ExportColumn[],
  source: Record<string, unknown>,
  overrides: Record<string, string>,
) =>
  Object.fromEntries(
    columns.map(({ key, numeric }) => [
      key,
      key in overrides ? overrides[key] : toCellValue(source[key], numeric),
    ]),
  )

const compareStrata = (a: string | null, b: string | null) => {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1
  return collator.compare(a, b)
}

const compareDepth = (a: unknown, b: unknown) =>
  (typeof a === 'number' ? a : -Infinity) - (typeof b === 'number' ? b : -Infinity)

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const buildSOCSampleExport = (
  sample: SOCSampleWithDocuments,
  lookups: SOCSampleExportLookups,
): SOCSampleExport => {
  const strataNames = labelById(lookups.strata, stratum => stratum.name)
  const dapMethods = labelById(lookups.dapMethods, method => method.es_AR ?? '')
  const soilTextures = labelById(lookups.soilTextures, texture => texture.es_AR_long ?? '')
  const activitiesById = new Map(lookups.activities.map(activity => [activity.id, activity]))
  const samplingNumber = resolve(
    labelById(lookups.samplingNumbers, item => item.es_AR ?? ''),
    sample.samplingNumberId,
  )

  const strataRows = (sample.monitoringSOCSamplingAreaSamples ?? [])
    .filter(row => !row.isDeleted)
    .map(row => ({
      stratum: strataNames.get(row.samplingAreaId) ?? null,
      row: toRow(STRATA_COLUMNS, row, {
        samplingAreaId: strataNames.get(row.samplingAreaId) ?? UNRESOLVED_STRATUM,
        soilTextureTypeId: resolve(soilTextures, row.soilTextureTypeId),
      }),
    }))
    .sort((a, b) => compareStrata(a.stratum, b.stratum))
    .map(({ row }) => row)

  const sitesRows = (sample.monitoringSOCSitesSamples ?? [])
    .filter(row => !row.isDeleted)
    .map(row => {
      const monitoringSite = activitiesById.get(row.monitoringSOCActivityId)?.monitoringSite
      const stratum = strataNames.get(monitoringSite?.samplingAreaId) ?? null
      const site = monitoringSite?.name ?? ''
      return {
        stratum,
        site,
        initialDepth: row.initialDepth,
        row: toRow(SITES_COLUMNS, row, {
          stratum: stratum ?? UNRESOLVED_STRATUM,
          site,
          monitoringSOCActivityId: activitiesById.get(row.monitoringSOCActivityId)?.key ?? '',
          dapMethodId: resolve(dapMethods, row.dapMethodId),
        }),
      }
    })
    .sort(
      (a, b) =>
        compareStrata(a.stratum, b.stratum) ||
        collator.compare(a.site, b.site) ||
        compareDepth(a.initialDepth, b.initialDepth),
    )
    .map(({ row }) => row)

  const headerInfo = [
    { label: 'Establecimiento', value: lookups.farm.name },
    { label: 'Nodo', value: lookups.farm.hubName ?? '' },
    { label: 'Número de muestreo', value: samplingNumber },
    { label: 'Fecha de muestreo', value: dayjs(sample.sampleDate).format('DD/MM/YYYY') },
    {
      label: 'Laboratorio',
      value: resolve(
        labelById(lookups.laboratories, lab => lab.label),
        sample.laboratoryId,
      ),
    },
    {
      label: 'Protocolo SOC',
      value: resolve(
        labelById(lookups.socProtocols, protocol => protocol.label),
        sample.socProtocolId,
      ),
    },
  ]

  const fileName = [
    'resultados-suelos',
    slugify(lookups.farm.name),
    slugify(samplingNumber),
    dayjs(sample.sampleDate).format('YYYY-MM-DD'),
  ].join('_')

  return {
    fileName: `${fileName}.xlsx`,
    headerInfo,
    sheets: [
      { name: 'Estratos', columns: STRATA_COLUMNS, rows: strataRows },
      { name: 'SOC y DAP', columns: SITES_COLUMNS, rows: sitesRows },
    ],
  }
}
